import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SelectOption } from '@vchasno/ui-kit';

import {
  DocumentActionType,
  HrsDocumentCategory,
  TaskDocumentAccessSettings,
  TaskRetrieveSerializer,
  appApi,
} from 'api/api';
import { captureLog } from 'lib/sentry';

import {
  UniversalDocument,
  UniversalDocumentProcess,
  UniversalDocumentSettingsSection,
  UniversalDocumentSigner,
  UniversalDocumentState,
  UniversalDocumentStep,
} from './universalDocument.types';
import {
  buildStateFromRetrieveTask,
  findActiveDocument,
} from './universalDocument.utils';

const initialState: UniversalDocumentState = {
  process: UniversalDocumentProcess.CREATE,
  step: UniversalDocumentStep.INITIATOR,
  allowedSteps: [
    UniversalDocumentStep.INITIATOR,
    UniversalDocumentStep.DOCUMENTS,
    UniversalDocumentStep.SETTINGS,
  ],
  initiator: null,
  company: null,
  taskId: null,
  isUniqueDocumentSettings: false,
  documents: {},
  activeDocumentId: null,
  activeDocumentSection: UniversalDocumentSettingsSection.SIGNERS,
  allowedDocumentSections: [
    UniversalDocumentSettingsSection.SIGNERS,
    UniversalDocumentSettingsSection.ACCESS_SETTINGS,
  ],
};

export const universalDocumentSlice = createSlice({
  name: 'universalDocument',
  initialState,
  reducers: {
    setCompany: (state, action: PayloadAction<Nullable<SelectOption>>) => {
      state.company = action.payload;

      if (state.initiator?.value !== 'new') {
        state.initiator = null;
      }
    },
    setInitiator: (state, action: PayloadAction<Nullable<SelectOption>>) => {
      state.initiator = action.payload;
    },
    setTaskId: (state, action: PayloadAction<string>) => {
      state.taskId = action.payload;
    },
    removeDocument: (state, action: PayloadAction<string>) => {
      delete state.documents[action.payload];

      if (state.activeDocumentId === action.payload) {
        const documentId = Object.keys(state.documents)[0];

        state.activeDocumentId = documentId || null;
        state.activeDocumentSection = UniversalDocumentSettingsSection.SIGNERS;
      }
    },

    changeDocumentName: (state, action: PayloadAction<string>) => {
      const document = findActiveDocument(state);

      if (!document) return state;

      document.file.name = action.payload;
    },

    setActiveDocumentType: (
      state,
      action: PayloadAction<Nullable<HrsDocumentCategory>>,
    ) => {
      const document = findActiveDocument(state);

      if (!document) return state;

      document.file.category = action.payload;
    },

    setActiveDocumentNumber: (
      state,
      action: PayloadAction<Nullable<string>>,
    ) => {
      const document = findActiveDocument(state);

      if (!document) return state;

      document.file.number = action.payload;
    },

    toggleActiveDocumentIsParallel: (state) => {
      const document = findActiveDocument(state);

      if (!document) return state;

      const value = !document.isParallel;

      if (state.isUniqueDocumentSettings) {
        document.isParallel = value;
      } else {
        for (const doc in state.documents) {
          state.documents[doc].isParallel = value;
        }
      }
    },

    addDocumentSigner: (state, action: PayloadAction<SelectOption>) => {
      const [fullName, position] = action.payload?.label.split('|') || [];

      const signer: UniversalDocumentSigner = {
        actionType: DocumentActionType.Sign,
        fullName,
        position: position?.trim() || null,
        id: action.payload.value,
        deletable: true,
      };

      if (state.isUniqueDocumentSettings) {
        const document = findActiveDocument(state);

        if (!document) return state;

        document.signers.push(signer);
      } else {
        for (const doc in state.documents) {
          state.documents[doc].signers.push(signer);
        }
      }
    },
    removeDocumentSigner: (state, action: PayloadAction<string>) => {
      if (state.isUniqueDocumentSettings) {
        const document = findActiveDocument(state);

        if (!document) return state;

        document.signers = document.signers.filter(
          (signer) => signer.id !== action.payload,
        );
      } else {
        for (const doc in state.documents) {
          state.documents[doc].signers = state.documents[doc].signers.filter(
            (signer) => signer.id !== action.payload,
          );
        }
      }
    },
    changeActiveDocumentAccessSettings: (
      state,
      action: PayloadAction<TaskDocumentAccessSettings>,
    ) => {
      if (state.isUniqueDocumentSettings) {
        const document = findActiveDocument(state);

        if (!document) return state;

        document.accessSettings = action.payload;
      } else {
        for (const doc in state.documents) {
          state.documents[doc].accessSettings = action.payload;
        }
      }
    },
    toggleActiveDocumentSignerActionType: (
      state,
      action: PayloadAction<string>,
    ) => {
      const document = findActiveDocument(state);

      if (!document) return state;

      const signer = document.signers.find(
        (signer) => signer.id === action.payload,
      );

      if (!signer) return state;

      signer.actionType =
        signer.actionType === DocumentActionType.View
          ? DocumentActionType.Sign
          : DocumentActionType.View;
    },

    reorderActiveDocumentSigners: (state, action: PayloadAction<number[]>) => {
      const document = findActiveDocument(state);

      if (!document) return state;

      const newSigners: UniversalDocument['signers'] = [];

      for (const index of action.payload) {
        if (document.signers[index]) {
          newSigners.push(document.signers[index]);
        }
      }

      document.signers = newSigners;
    },

    toggleUniqueDocumentSettings: (state) => {
      state.isUniqueDocumentSettings = !state.isUniqueDocumentSettings;

      if (state.isUniqueDocumentSettings) {
        state.allowedDocumentSections.unshift(
          UniversalDocumentSettingsSection.FILE,
        );
      } else {
        state.allowedDocumentSections = state.allowedDocumentSections.filter(
          (section) => section !== UniversalDocumentSettingsSection.FILE,
        );

        if (
          state.activeDocumentSection === UniversalDocumentSettingsSection.FILE
        ) {
          state.activeDocumentSection =
            UniversalDocumentSettingsSection.SIGNERS;
        }

        const documents: Record<string, UniversalDocument> = {};

        const activeDocument =
          findActiveDocument(state) || Object.values(state.documents)[0];

        for (const doc in state.documents) {
          documents[doc] = {
            ...activeDocument,
            id: state.documents[doc].id,
            file: state.documents[doc].file,
          };
        }

        state.documents = documents;
      }
    },
    setActiveDocumentId: (state, action: PayloadAction<string>) => {
      state.activeDocumentId = action.payload;
    },
    nextStep: (state) => {
      const currentStepIndex = state.allowedSteps.indexOf(state.step);
      const nextStepIndex = currentStepIndex + 1;

      if (nextStepIndex < state.allowedSteps.length) {
        state.step = state.allowedSteps[nextStepIndex];
      } else {
        state.step = UniversalDocumentStep.SETTINGS;

        captureLog('No next step available', {
          step: state.step,
          allowedSteps: state.allowedSteps,
          nextStepIndex,
        });
      }
    },
    toggleDocumentSection: (
      state,
      action: PayloadAction<UniversalDocumentSettingsSection>,
    ) => {
      const section = action.payload;

      if (state.activeDocumentSection === section) {
        state.activeDocumentSection = null;
      } else {
        state.activeDocumentSection = section;
      }
    },
    start: (_, action: PayloadAction<Partial<UniversalDocumentState>>) => {
      return {
        ...initialState,
        ...action.payload,
      };
    },
    startEditing: (state, action: PayloadAction<TaskRetrieveSerializer>) => {
      const nextState = buildStateFromRetrieveTask(
        {
          ...state,
          process: UniversalDocumentProcess.EDIT,
        },
        action.payload,
      );

      nextState.taskId = action.payload.id;
      nextState.step = UniversalDocumentStep.SETTINGS;

      return nextState;
    },

    reset: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      appApi.endpoints.retrieveTask.matchFulfilled,
      (state, action) => {
        if (action.payload?.detail.id !== state.taskId) return state;

        const task = action.payload.detail;

        return buildStateFromRetrieveTask(state, task);
      },
    );
  },
  selectors: {
    selectStep: (state) => state.step,
    selectProcess: (state) => state.process,
    selectTaskId: (state) => state.taskId,
    selectAllowedSteps: (state) => state.allowedSteps,
    selectInitiator: (state) => state.initiator,
    selectCompany: (state) => state.company,
    selectIsUniqueDocumentSettings: (state) => state.isUniqueDocumentSettings,
    selectDocuments: (state) => state.documents,
    selectHasDocuments: (state) => {
      const documents = Object.values(state.documents);

      return documents.length > 0;
    },
    selectActiveDocumentId: (state) => state.activeDocumentId,
    selectActiveDocument: findActiveDocument,
    selectActiveDocumentSection: (state) => state.activeDocumentSection,
    selectAllowedDocumentSections: (state) => state.allowedDocumentSections,
    selectActiveDocumentSigners: (state) => {
      const document = findActiveDocument(state);

      if (!document) return [];

      return document.signers;
    },
    selectActiveDocumentAccessType: (state) => {
      const document = findActiveDocument(state);

      if (!document) return null;

      return document.accessSettings;
    },
  },
});
